import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getRecentProfilesForNav, getAllProfilesForUser, updateProfileLastViewed, createProfileDocument } from '../services/accountService'
import { isMinorBirthDate } from '../utils/profileAge'
import type { ProfileDocument } from '../types/account'

interface UseProfilesReturn {
  recentProfiles: ProfileDocument[]
  allProfiles: ProfileDocument[]
  totalProfiles: number
  hasMoreThan5: boolean
  isLoading: boolean
  error: string | null
  refreshProfiles: () => Promise<void>
  selectProfile: (profileId: string) => Promise<void>
  createProfile: (profileData: {
    name: string
    relationship?: string
    dateOfBirth?: string
    gender?: string
    default?: boolean
  }) => Promise<ProfileDocument>
}

export const useProfiles = (): UseProfilesReturn => {
  const { currentUser, defaultProfile, isAuthReady } = useAuth()
  const [recentProfiles, setRecentProfiles] = useState<ProfileDocument[]>([])
  const [allProfiles, setAllProfiles] = useState<ProfileDocument[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchProfiles = useCallback(async () => {
    if (!currentUser?.uid || !isAuthReady) {
      setIsLoading(false)  // Fix: Set loading to false when no user or auth not ready
      return
    }

    // If we have a defaultProfile from AuthContext and no existing profiles, use it
    // But still query Firestore to get all profiles (in case user has multiple)
    if (defaultProfile && recentProfiles.length === 0) {
      setRecentProfiles([defaultProfile])
      setAllProfiles([defaultProfile])
      setIsLoading(false)
      // Continue to query Firestore in background to get complete profile list
    }

    setIsLoading(true)
    setError(null)

    let attempts = 0
    let all: ProfileDocument[] = []
    let recent: ProfileDocument[] = []

    while (attempts < 3) { // Try up to 3 times (1 second max)
      try {
        const [recentResult, allResult] = await Promise.all([
          getRecentProfilesForNav(currentUser.uid),
          getAllProfilesForUser(currentUser.uid) // No pagination - gets all profiles for backward compatibility
        ])
        recent = recentResult
        // Handle new return format
        all = Array.isArray(allResult) ? allResult : allResult.profiles
        // Break on success OR if we have a valid empty result (no profiles)
        if (all.length > 0 || attempts > 0) break
      } catch (err) {
        console.error(`[useProfiles] Error fetching profiles (attempt ${attempts + 1}):`, err)
        setError(err instanceof Error ? err.message : 'Failed to fetch profiles')
        setRecentProfiles([])
        setAllProfiles([])
        setIsLoading(false)
        return
      }
      attempts++
      await new Promise(res => setTimeout(res, 300))
    }

    setRecentProfiles(recent)
    setAllProfiles(all)
    setIsLoading(false)
  }, [currentUser?.uid, defaultProfile, isAuthReady])

  const selectProfile = useCallback(async (profileId: string) => {
    try {
      // Update last_viewed timestamp
      await updateProfileLastViewed(profileId)
      
      // Refresh profiles to get updated order
      await fetchProfiles()
      
      // Navigate to profile (this will be handled by the calling component)
    } catch (err) {
      console.error('Error selecting profile:', err)
      setError(err instanceof Error ? err.message : 'Failed to select profile')
      throw err
    }
  }, [fetchProfiles])

  const refreshProfiles = useCallback(async () => {
    await fetchProfiles()
  }, [fetchProfiles])

  /**
   * Programmatic profile creation.
   *
   * Date of birth is required: profiles for anyone under 18 need a recorded
   * guardian-authority attestation, which cannot be captured here. Any caller
   * that needs to create a minor's profile must go through the Add Profile form
   * so the attestation is shown and stored.
   */
  const createProfile = useCallback(async (profileData: {
    name: string
    relationship?: string
    dateOfBirth: string
    gender?: string
    default?: boolean
  }) => {
    if (!currentUser?.uid) {
      throw new Error('User not authenticated')
    }

    if (!profileData.dateOfBirth) {
      throw new Error('Date of birth is required to create a profile')
    }

    if (isMinorBirthDate(profileData.dateOfBirth)) {
      throw new Error(
        'Profiles for anyone under 18 must be created through the Add Profile form, ' +
        'which records the required guardian-authority attestation'
      )
    }

    try {
      const newProfile = await createProfileDocument(currentUser.uid, {
        name: profileData.name,
        relationship: profileData.relationship,
        birthDate: profileData.dateOfBirth,
        gender: profileData.gender,
        defaultProfile: profileData.default
      })

      // Refresh profiles to include the new one
      await fetchProfiles()

      return newProfile
    } catch (error) {
      console.error('Error creating profile:', error)
      throw error
    }
  }, [currentUser?.uid, fetchProfiles])

  useEffect(() => {
    fetchProfiles()
  }, [fetchProfiles])

  return {
    recentProfiles,
    allProfiles,
    totalProfiles: allProfiles.length,
    hasMoreThan5: allProfiles.length > 5,
    isLoading,
    error,
    refreshProfiles,
    selectProfile,
    createProfile
  }
}